// 「全国で高い方から上位 3%（1,892 中 57 位）」を答えるための索引。指標ごとに非 null 値を
// 昇順で 1 本持ち、二分探索で順位を引く。全件走査を毎回やらずに済むよう、
// データ読み込み時に 1 度だけ構築する。
import type { Muni } from "../../schema/muni";
import { DEFAULT_LANG, t, type Lang } from "./i18n";
import { getMetrics, isRankable, METRIC_BETTER, METRIC_GET, METRIC_ORDER, type MetricKey } from "./metrics";

export type RankIndex = Record<MetricKey, number[]>;

/** 全国順位。良い端・悪い端の両方から数えた 1 始まりの位置を持つ。 */
export interface RankPosition {
  total: number;
  /** 望ましい端から数えた順位。同値は同順位（厳密に良い値の数 + 1）。 */
  fromBetter: number;
  /** 望ましくない端から数えた順位。同じく同値は同順位。 */
  fromWorse: number;
}

export function buildRankIndex(munis: Muni[]): RankIndex {
  const entries = METRIC_ORDER.map((key) => {
    const get = METRIC_GET[key];
    const values = munis
      .filter((m) => isRankable(key, m))
      .map((m) => get(m))
      .filter((v): v is number => v !== null)
      .sort((a, b) => a - b);
    return [key, values] as const;
  });
  return Object.fromEntries(entries) as RankIndex;
}

/** sorted の中で value より小さい要素の個数（lower bound）。 */
function countBelow(sorted: number[], value: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((sorted[mid] ?? 0) < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** sorted の中で value 以下の要素の個数（upper bound）。同値をまたいで数えるために使う。 */
function countAtMost(sorted: number[], value: number): number {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((sorted[mid] ?? 0) <= value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** 0..1。value より小さい値の割合 = 「低い方から何割の位置か」。位置バーのマーカーがこれを見る。 */
export function percentileBelow(index: RankIndex, metric: MetricKey, value: number): number {
  const sorted = index[metric];
  if (sorted.length === 0) return 0;
  return countBelow(sorted, value) / sorted.length;
}

/** 良い端・悪い端の双方から数えた順位。どちらの端から語るかは rankLabel が選ぶ。 */
export function rankPosition(index: RankIndex, metric: MetricKey, value: number): RankPosition {
  const sorted = index[metric];
  const total = sorted.length;
  const below = countBelow(sorted, value);
  const above = total - countAtMost(sorted, value);
  return METRIC_BETTER[metric] === "high"
    ? { total, fromBetter: above + 1, fromWorse: below + 1 }
    : { total, fromBetter: below + 1, fromWorse: above + 1 };
}

interface NearEnd {
  word: string;
  percent: number;
  position: number;
  total: number;
}

/**
 * 近い方の端を選ぶ。「低い方から 97%」は良い値と読み違えられたので、
 * 悪い側にある値は悪い端から数え、良い側にある値は良い端から数える。
 * どちらの端から数えたかを形容詞で必ず添えるため、向きが文だけで決まる。
 */
function nearEnd(index: RankIndex, metric: MetricKey, value: number, lang: Lang): NearEnd | null {
  const { total, fromBetter, fromWorse } = rankPosition(index, metric, value);
  if (total === 0) return null;
  const def = getMetrics(lang)[metric];
  const fromGoodEnd = fromBetter <= total / 2;
  const position = fromGoodEnd ? fromBetter : fromWorse;
  return {
    word: fromGoodEnd ? def.betterWord : def.worseWord,
    // 1,892 中 1 位を「上位 0%」と書かないよう下限を 1% にする
    percent: Math.max(1, Math.round((position / total) * 100)),
    position,
    total,
  };
}

/** 詳細ページ用。向き・割合・実順位まで書く。順位を引けない指標では空文字を返す。 */
export function rankLabel(index: RankIndex, metric: MetricKey, value: number, lang: Lang = DEFAULT_LANG): string {
  const end = nearEnd(index, metric, value, lang);
  if (!end) return "";
  return t(lang).rankLabel(end.word, end.percent, end.position, end.total);
}

/** 地図の点のツールチップ用。同じ言い回しのまま「（N 中 M 位）」だけを落とした短い形。 */
export function rankLabelShort(index: RankIndex, metric: MetricKey, value: number, lang: Lang = DEFAULT_LANG): string {
  const end = nearEnd(index, metric, value, lang);
  if (!end) return "";
  return t(lang).rankLabelShort(end.word, end.percent);
}
